import React, { useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Check, Pencil, Undo2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { formatHeldDate } from "../lib/calibrationStage";

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const ConversationHeldCard = ({ cal, onChange }) => {
  const [editing, setEditing] = useState(!cal.conversation_held_on);
  const [heldOn, setHeldOn] = useState(cal.conversation_held_on || today());
  const [notes, setNotes] = useState(cal.conversation_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!heldOn) { toast.error("Pick the date the conversation took place"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/calibrations/${cal.id}/conversation`, { held_on: heldOn, notes: notes.trim() || null });
      onChange({ conversation_held_on: r.data.conversation_held_on, conversation_notes: r.data.conversation_notes });
      setEditing(false);
      toast.success("Conversation marked as held");
    } catch (err) { toast.error(apiError(err, "Could not save")); }
    finally { setSaving(false); }
  };

  const undo = async () => {
    setSaving(true);
    try {
      await api.delete(`/calibrations/${cal.id}/conversation`);
      onChange({ conversation_held_on: null, conversation_notes: null });
      setHeldOn(today()); setNotes(""); setEditing(true);
      toast.success("Conversation unmarked");
    } catch (err) { toast.error(apiError(err, "Could not undo")); }
    finally { setSaving(false); }
  };

  return (
    <div id="conversation-card" className="cal-card cal-shadow p-6 md:p-8 mb-8" data-testid="conversation-card">
      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2 flex items-center gap-2">
        <MessageSquareText size={12} /> [ CONVERSATION ]
      </div>
      {!editing ? (
        <>
          <h2 className="font-display font-bold text-2xl mb-1 flex items-center gap-2" data-testid="conversation-held-heading">
            <Check size={20} className="text-[#00A36C]" /> Held on {formatHeldDate(cal.conversation_held_on)}
          </h2>
          {cal.conversation_notes ? (
            <p className="text-sm text-[#111] whitespace-pre-wrap mt-3 border-l-2 border-[#FF4F00] pl-3" data-testid="conversation-notes-display">{cal.conversation_notes}</p>
          ) : (
            <p className="text-sm text-[#666] mt-2">No meeting notes added.</p>
          )}
          <p className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mt-4">Private to you — not shared with the employee</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={() => setEditing(true)} className="cal-btn cal-btn-ghost" data-testid="conversation-edit-btn"><Pencil size={14} /> Edit</button>
            <button onClick={undo} disabled={saving} className="cal-btn cal-btn-ghost" data-testid="conversation-undo-btn"><Undo2 size={14} /> Unmark</button>
          </div>
        </>
      ) : (
        <>
          <h2 className="font-display font-bold text-2xl mb-2">Mark the conversation as held</h2>
          <p className="text-[#666] text-sm mb-4">Once you've talked through the comparison with {cal.employee_name.split(" ")[0]}, record the date to complete this calibration. Notes are private to you.</p>
          <div className="space-y-3">
            <div>
              <label className="block font-mono-cal text-[10px] uppercase tracking-widest text-[#666]" htmlFor="conv-date">Date held</label>
              <input id="conv-date" type="date" max={today()} value={heldOn} onChange={(e) => setHeldOn(e.target.value)} className="cal-input block sm:max-w-xs mt-1" data-testid="conversation-date-input" />
            </div>
            <div>
              <label className="block font-mono-cal text-[10px] uppercase tracking-widest text-[#666]" htmlFor="conv-notes">Meeting notes (optional)</label>
              <textarea id="conv-notes" rows={4} maxLength={4000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Agreed actions, follow-ups, anything to remember next time…" className="cal-input mt-1" data-testid="conversation-notes-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={save} disabled={saving} className="cal-btn" data-testid="conversation-save-btn"><Check size={14} /> {saving ? "Saving…" : "Mark as held"}</button>
              {cal.conversation_held_on && (
                <button onClick={() => { setHeldOn(cal.conversation_held_on); setNotes(cal.conversation_notes || ""); setEditing(false); }} disabled={saving} className="cal-btn cal-btn-ghost" data-testid="conversation-cancel-btn">Cancel</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConversationHeldCard;
